import { useMemo, useState } from "preact/hooks";
import type { Company, InvoiceLineItem, Opportunity } from "../../types";
import { DEFAULT_TAX_RATE, PAYMENT_TERMS_DAYS, PRODUCT_CATALOG } from "../../types";
import { Modal } from "../ui/Modal";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";
import { computeInvoiceTotals } from "../../lib/billing";
import { money, isoDateOnly, isoDaysFromNow } from "../../lib/format";

interface DraftLine {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

let keyCounter = 0;
function emptyLine(): DraftLine {
  keyCounter += 1;
  return { key: `nl${keyCounter}`, description: "", quantity: "1", unitPrice: "" };
}

export function InvoiceFormModal({
  companies,
  opportunities,
  fromOpportunity,
  onClose,
  onCreated,
}: {
  companies: Company[];
  opportunities: Opportunity[];
  fromOpportunity?: Opportunity;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
}) {
  const prefillCompany = fromOpportunity?.companyId ?? companies[0]?.id ?? "";
  const [companyId, setCompanyId] = useState(prefillCompany);
  const [opportunityId, setOpportunityId] = useState(fromOpportunity?.id ?? "");
  const [taxRate, setTaxRate] = useState(String(DEFAULT_TAX_RATE));
  const [dueDate, setDueDate] = useState(isoDateOnly(isoDaysFromNow(PAYMENT_TERMS_DAYS)));
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<DraftLine[]>(() => {
    if (fromOpportunity) {
      const product = PRODUCT_CATALOG.find((p) => p.id === fromOpportunity.productId);
      const desc = `${product?.name ?? "Services"} — ${fromOpportunity.name}`;
      return [{ key: "prefill", description: desc, quantity: "1", unitPrice: String(fromOpportunity.finalValue ?? fromOpportunity.value ?? "") }];
    }
    return [emptyLine()];
  });

  const wonForCompany = useMemo(
    () => opportunities.filter((o) => o.companyId === companyId && o.stage === "Won"),
    [opportunities, companyId]
  );

  const preview = useMemo(() => {
    const lineItems: InvoiceLineItem[] = lines.map((l, idx) => ({
      id: `p${idx}`,
      description: l.description,
      quantity: Number(l.quantity) || 0,
      unitPrice: Number(l.unitPrice) || 0,
    }));
    return computeInvoiceTotals({ lineItems, taxRate: Number(taxRate) || 0, amountPaid: 0 });
  }, [lines, taxRate]);

  function setLine(key: string, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function submit(e: Event) {
    e.preventDefault();
    if (!companyId) {
      setError("A company is required.");
      return;
    }
    const lineItems: InvoiceLineItem[] = lines
      .filter((l) => l.description.trim() && Number(l.unitPrice) > 0 && Number(l.quantity) > 0)
      .map((l) => ({ id: `li-${Math.random().toString(36).slice(2, 8)}`, description: l.description.trim(), quantity: Number(l.quantity), unitPrice: Number(l.unitPrice) }));
    if (lineItems.length === 0) {
      setError("Add at least one line item with a description, quantity and price.");
      return;
    }
    const invoice = store.addInvoice({
      companyId,
      opportunityId: opportunityId || undefined,
      issueDate: new Date().toISOString(),
      dueDate: new Date(dueDate).toISOString(),
      lineItems,
      taxRate: Number(taxRate) || 0,
      status: "Draft",
      amountPaid: 0,
      notes: notes.trim() || undefined,
    });
    toast.success(`Invoice ${invoice.invoiceNumber} created as draft.`);
    onCreated(invoice.id);
  }

  return (
    <Modal title={fromOpportunity ? "Generate Invoice" : "New Invoice"} onClose={onClose} width={620}>
      <form class="form" onSubmit={submit}>
        {error && <p class="form-error">{error}</p>}
        <div class="form-row">
          <label>
            Company
            <select
              data-ai-id="company"
              value={companyId}
              disabled={!!fromOpportunity}
              onChange={(e) => {
                setCompanyId((e.target as HTMLSelectElement).value);
                setOpportunityId("");
              }}
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Linked deal (optional)
            <select data-ai-id="opportunity" value={opportunityId} disabled={!!fromOpportunity} onChange={(e) => setOpportunityId((e.target as HTMLSelectElement).value)}>
              <option value="">Not linked</option>
              {fromOpportunity && <option value={fromOpportunity.id}>{fromOpportunity.name}</option>}
              {!fromOpportunity &&
                wonForCompany.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div class="line-editor">
          <div class="line-editor__head">
            <span>Description</span>
            <span>Qty</span>
            <span>Unit price €</span>
            <span>Amount</span>
            <span />
          </div>
          {lines.map((l) => (
            <div class="line-editor__row" key={l.key}>
              <input data-ai-id={`line-description-${l.key}`} placeholder="Plan or service" value={l.description} onInput={(e) => setLine(l.key, { description: (e.target as HTMLInputElement).value })} />
              <input type="number" min="1" data-ai-id={`line-quantity-${l.key}`} value={l.quantity} onInput={(e) => setLine(l.key, { quantity: (e.target as HTMLInputElement).value })} />
              <input type="number" min="0" data-ai-id={`line-unit-price-${l.key}`} value={l.unitPrice} onInput={(e) => setLine(l.key, { unitPrice: (e.target as HTMLInputElement).value })} />
              <span class="line-editor__amount">{money((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0))}</span>
              <button type="button" class="line-editor__remove" aria-label="Remove line" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : ls))}>
                ×
              </button>
            </div>
          ))}
          <button type="button" class="btn btn--secondary btn--small" onClick={() => setLines((ls) => [...ls, emptyLine()])}>
            + Add line
          </button>
        </div>

        <div class="form-row">
          <label>
            Tax rate (%)
            <input type="number" min="0" max="100" data-ai-id="tax-rate" value={taxRate} onInput={(e) => setTaxRate((e.target as HTMLInputElement).value)} />
          </label>
          <label>
            Due date
            <input type="date" data-ai-id="due-date" value={dueDate} onInput={(e) => setDueDate((e.target as HTMLInputElement).value)} />
          </label>
        </div>

        <label>
          Notes
          <textarea data-ai-id="notes" value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </label>

        <div class="invoice-totals">
          <div>
            <span>Subtotal</span>
            <span>{money(preview.subtotal)}</span>
          </div>
          <div>
            <span>Tax ({Number(taxRate) || 0}%)</span>
            <span>{money(preview.taxAmount)}</span>
          </div>
          <div class="invoice-totals__grand">
            <span>Total</span>
            <span>{money(preview.total)}</span>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" class="btn btn--primary" data-ai-id="submit-create-invoice">
            Create Invoice
          </button>
        </div>
      </form>
    </Modal>
  );
}
